import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { schedulesApi, schedulesQueryKey } from '@/features/schedules/schedules.api';

interface ScheduleToggleProps {
  orgId: string;
  projectId: string;
  /** Schedule kind, e.g. 'generate_content' | 'analyze' | 'sync_meta_insights'. */
  kind: string;
  /** Human label shown next to the switch. */
  label: string;
  /** Short hint shown on hover (what enabling does + cadence). */
  hint?: string;
  /** Hide the control (e.g. for non-admins). */
  canEdit?: boolean;
}

/**
 * A state-aware autonomous-schedule toggle. Unlike a fire-and-forget button, it
 * reflects the real on/off state (a filled switch + an "On"/"Off" tag) and flips
 * it via the generic schedules API. Reused across the app so every "enable
 * autonomous …" control looks and behaves the same.
 */
export function ScheduleToggle({
  orgId,
  projectId,
  kind,
  label,
  hint,
  canEdit = true,
}: ScheduleToggleProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = schedulesQueryKey(orgId, projectId);

  const query = useQuery({ queryKey: key, queryFn: () => schedulesApi.list(orgId, projectId) });
  const enabled = query.data?.find((s) => s.kind === kind)?.enabled ?? false;

  const mutation = useMutation({
    mutationFn: (next: boolean) => schedulesApi.toggle(orgId, projectId, kind, next),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: key });
      message.success(res.enabled ? `${label} — on` : `${label} — off`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not update'),
  });

  const control = (
    <Space size={8} align="center">
      <Switch
        size="small"
        checked={enabled}
        loading={query.isLoading || mutation.isPending}
        disabled={!canEdit}
        onChange={(v) => mutation.mutate(v)}
        aria-label={label}
      />
      <Typography.Text
        {...(enabled ? {} : { type: 'secondary' as const })}
        style={{ fontSize: 13 }}
      >
        {label}
      </Typography.Text>
      <Tag color={enabled ? 'green' : 'default'} style={{ marginInlineStart: 0 }}>
        {enabled ? 'On' : 'Off'}
      </Tag>
    </Space>
  );

  return hint ? <Tooltip title={hint}>{control}</Tooltip> : control;
}
